/**
 * @fileoverview Presentational layout for the NotificationForm page.
 * @module pages/notificationForm/NotificationFormView
 */
import styles from "../../styles/NotificationForm.module.css";
import { compactSelectStyles } from "../../utils/reactSelectControlStyles.js";
import LanguageSelector from "../../components/LanguageSelector/index.jsx";
import NotificationBell from "../../components/NotificationBell/index.jsx";
import { Button, CircularProgress, Backdrop } from "@mui/material";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Select from "react-select";
import { ToastContainer } from "react-toastify";
import { ReactComponent as SaveIcon } from "../../assets/icons/CompanyEditPage/SaveIcon.svg";
import { ReactComponent as TitleIcon } from "../../assets/icons/EventCreation/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/EventCreation/ChevronLeftIcon.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/Calendar_Today.svg";
import {
  getSelectedOption,
  getSelectedOptionWithZeroFirst,
} from "../../utils/common.js";
import {
  NOTIFICATION_TYPE_EMAIL,
  NOTIFICATION_TYPE_APP,
  NOTIFICATION_TYPE_WHATSAPP,
} from "./notificationFormConstants.js";
import NotificationFormImageDropzone from "./NotificationFormImageDropzone.jsx";
import NotificationFormSubjectFields from "./NotificationFormSubjectFields.jsx";

/** Renders the NotificationForm layout from a pre-built view-model. */
const NotificationFormView = ({
  allowed,
  t,
  navigate,
  locale,
  formData,
  setFormData,
  imagePreview,
  imageRef,
  selectSubsectorRef,
  sectors,
  subsectors,
  setSubsectors,
  showError,
  loading,
  isCalendarOpen,
  setIsCalendarOpen,
  error,
  notificationTypeOptions,
  companySizeOptions,
  companyGeneralActivityOptions,
  handlers,
}) => {
  if (!allowed) {
    return null;
  }

  const imageDropzoneProps = {
    imageRef,
    imagePreview,
    imagenUrl: formData.imagenUrl,
    error,
    onDragEnter: handlers.onDragEnter,
    onDragLeave: handlers.onDragLeave,
    onDrop: handlers.onDrop,
    onImageChange: handlers.handleImageChange,
  };

  const subjectFieldProps = {
    asunto: formData.asunto,
    descripcion: formData.descripcion,
    showError,
    onAsuntoChange: handlers.handleAsuntoChange,
    onDescripcionChange: handlers.handleDescripcionChange,
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>
        <div className={styles.backNavigation}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
        </div>
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>
              {t("pages.notificationForm.header")}
            </h1>
          </div>
        </div>
      </div>

      <div className={styles.mainContainer}>
        <div className={styles.selectContainer}>
          <label className={styles.label}>
            {t("pages.notificationForm.notificationType")}
            <span className={styles.required}> *</span>
          </label>
          <Select
            options={notificationTypeOptions}
            value={formData.tipo}
            onChange={handlers.handleNotificationTypeChange}
            placeholder={t("pages.notificationForm.notificationTypePlaceholder")}
            styles={compactSelectStyles}
          />
        </div>

        <div className={styles.formContainer}>
          <div className={styles.sectionTitle}>
            <span>{t("pages.notificationForm.recipients")}</span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.checkboxesCol}>
              <label className={styles.label}>
                {t("pages.notificationForm.audience")}
                <span className={styles.required}> *</span>
              </label>
              <div className={styles.checkboxesContainer}>
                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={formData.audienciaParaSocios}
                    onChange={(e) =>
                      handlers.handleCheckboxChange(e, "audienciaParaSocios")
                    }
                  />
                  <label className={styles.checkboxLabel}>
                    {t("pages.notificationForm.audienceForMembers")}
                  </label>
                </div>
                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={formData.audienciaParaNoSocios}
                    onChange={(e) =>
                      handlers.handleCheckboxChange(e, "audienciaParaNoSocios")
                    }
                  />
                  <label className={styles.checkboxLabel}>
                    {t("pages.notificationForm.audienceForNonMembers")}
                  </label>
                </div>
              </div>
            </div>

            <div className={styles.formCol}>
              <label className={styles.inputLabel}>
                {t("pages.notificationForm.companySize")}
              </label>
              <Select
                value={getSelectedOption(
                  companySizeOptions,
                  formData.tamanoDeLaEmpresa,
                )}
                options={companySizeOptions}
                placeholder={t("pages.notificationForm.companySizePlaceholder")}
                isClearable={true}
                styles={compactSelectStyles}
                className="select__control"
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    tamanoDeLaEmpresa: selectedOption?.value ?? null,
                  }));
                }}
              />
            </div>

            <div className={styles.formCol}>
              <label className={styles.inputLabel}>
                {t("pages.notificationForm.sector")}
              </label>
              <Select
                options={sectors}
                placeholder={t("pages.notificationForm.sectorPlaceholder")}
                isClearable={true}
                styles={compactSelectStyles}
                className="select__control"
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    sector: selectedOption?._id ?? null,
                    subSector: null,
                  }));
                  selectSubsectorRef.current?.clearValue();
                  setSubsectors(selectedOption?.subSectores ?? []);
                }}
                getOptionValue={(option) => option.nombre}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>

            <div className={styles.formCol}>
              <label className={styles.inputLabel}>
                {t("pages.notificationForm.subsector")}
              </label>
              <Select
                ref={selectSubsectorRef}
                options={subsectors}
                placeholder={t("pages.notificationForm.subsectorPlaceholder")}
                isClearable={true}
                styles={compactSelectStyles}
                className="select__control"
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    subSector: selectedOption?._id ?? null,
                  }));
                }}
                getOptionValue={(option) => option.nombre}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formCol}>
              <label className={styles.inputLabel}>
                {t("pages.notificationForm.companyGeneralActivity")}
              </label>
              <Select
                value={getSelectedOptionWithZeroFirst(
                  companyGeneralActivityOptions,
                  formData.actividadGeneralDeLaEmpresa,
                )}
                placeholder={t(
                  "pages.notificationForm.companyGeneralActivityPlaceholder",
                )}
                isClearable={true}
                styles={compactSelectStyles}
                className="select__control"
                options={companyGeneralActivityOptions}
                onChange={(selectedOption) => {
                  setFormData((prev) => ({
                    ...prev,
                    actividadGeneralDeLaEmpresa: selectedOption?.value ?? null,
                  }));
                }}
              />
            </div>

            <div className={styles.formCol}>
              <label className={styles.inputLabel}>
                {t("pages.notificationForm.mainProduct")}
              </label>
              <input
                type="text"
                name="productoPrincipal"
                placeholder={t("pages.notificationForm.mainProductPlaceholder")}
                value={formData.productoPrincipal}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    productoPrincipal: e.target.value,
                  }))
                }
                className={styles.input}
                style={{
                  borderColor:
                    showError && !formData.productoPrincipal
                      ? "red"
                      : "#909092",
                }}
              />
            </div>

            <div className={styles.formCol}>
              <label className={styles.inputLabel}>
                {t("pages.notificationForm.dateOfCreationSac")}
              </label>
              <DatePicker
                name="fechaAltaSac"
                open={isCalendarOpen}
                value={
                  formData.fechaAltaSac ? dayjs(formData.fechaAltaSac) : null
                }
                onChange={handlers.handleDateChange}
                onOpenChange={setIsCalendarOpen}
                format="DD/MM/YYYY"
                inputReadOnly={true}
                suffixIcon={<CalendarIcon className={styles.calendarIcon} />}
                locale={locale}
                disabledDate={(current) => {
                  if (!current) {
                    return false;
                  }
                  const today = dayjs().endOf("day");
                  const maxDate = formData.fechaAltaSac
                    ? dayjs(formData.fechaAltaSac)
                    : today;
                  return current > maxDate;
                }}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            {formData?.tipo?.value === NOTIFICATION_TYPE_EMAIL && (
              <>
                <NotificationFormImageDropzone {...imageDropzoneProps} />
                <NotificationFormSubjectFields {...subjectFieldProps} />
              </>
            )}
            {formData?.tipo?.value === NOTIFICATION_TYPE_APP && (
              <NotificationFormSubjectFields {...subjectFieldProps} />
            )}
            {formData?.tipo?.value === NOTIFICATION_TYPE_WHATSAPP && (
              <>
                <NotificationFormImageDropzone {...imageDropzoneProps} />
                <NotificationFormSubjectFields
                  {...subjectFieldProps}
                  showDescription={false}
                />
              </>
            )}
          </div>

          <div className={styles.buttonsContainer}>
            <Button
              type="button"
              variant="contained"
              className={styles.uploadButton}
              onClick={handlers.handleCreateNotification}
              disabled={!handlers.isCreateEnabled()}
              startIcon={loading ? null : <SaveIcon />}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("pages.notificationForm.uploadButton")
              )}
            </Button>
          </div>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#4eabe7" }} />
      </Backdrop>
      <ToastContainer />
    </div>
  );
};

export default NotificationFormView;
